import axios from 'axios'
import {
  BrowserAuthError,
  CSRF_HEADER,
  getAuthGeneration,
  getCsrfToken,
  hasActiveBrowserSession,
  logoutBrowserSession,
  MissingCsrfTokenError,
  notifyAuthorizationChange,
  refreshBrowserSession,
  requireReauthentication,
} from './browserAuth'
import { purgeProtectedQueryCache } from '../queryClient'

const validateApiConfig = () => {
  const apiUrl = import.meta.env.VITE_API_URL

  if (!apiUrl) {
    if (import.meta.env.DEV) {
      return '/api/v1'
    }
    throw new Error(
      'VITE_API_URL environment variable is required in production/staging environments. ' +
        'Please set it in your .env file or environment configuration.',
    )
  }

  if (apiUrl.startsWith('http://') || apiUrl.startsWith('https://')) {
    try {
      new URL(apiUrl)
    } catch {
      throw new Error(
        `Invalid VITE_API_URL format: "${apiUrl}". ` +
          'It must be a valid URL (e.g., https://api.example.com/v1) or a relative path (e.g., /api/v1).',
      )
    }
  }

  return apiUrl
}

const API_BASE_URL = validateApiConfig()

const mailReviewItemPath = (reviewId) => {
  if (typeof reviewId !== 'string' || reviewId.trim() === '') {
    throw new TypeError('Mail Review item id is required.')
  }

  return `/mail-review/items/${encodeURIComponent(reviewId)}`
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true,
})

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])
const PRIVILEGED_PATHS = [
  '/leads',
  '/mail-review',
  '/maintenance',
  '/menu-permissions',
  '/roles',
  '/settings',
  '/users',
]

function getHeader(headers, name) {
  if (!headers) return null

  if (typeof headers.get === 'function') return headers.get(name)

  const key = Object.keys(headers).find(
    (header) => header.toLowerCase() === name.toLowerCase(),
  )

  return key ? headers[key] : null
}

function deleteHeader(headers, name) {
  if (!headers) return

  if (typeof headers.delete === 'function') {
    headers.delete(name)
    return
  }

  Object.keys(headers).forEach((header) => {
    if (header.toLowerCase() === name.toLowerCase()) delete headers[header]
  })
}

function setHeader(headers, name, value) {
  deleteHeader(headers, name)

  if (typeof headers.set === 'function') {
    headers.set(name, value)
    return
  }

  headers[name] = value
}

function hasExplicitBearer(config) {
  const authorization = getHeader(config?.headers, 'Authorization')
  return (
    typeof authorization === 'string' && /^Bearer\s+\S+$/i.test(authorization)
  )
}

function usesCookieAuthentication(config) {
  return config?.withCredentials !== false && !hasExplicitBearer(config)
}

function requestPath(config) {
  const url = config?.url
  if (typeof url !== 'string') return ''

  try {
    return new URL(url, 'https://admin.invalid').pathname.replace(
      /^\/api\/v1/,
      '',
    )
  } catch {
    return url
  }
}

function hasPrivilegedPath(config) {
  const path = requestPath(config)
  return PRIVILEGED_PATHS.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  )
}

function isMailReviewRequest(config) {
  const path = requestPath(config)
  return path === '/mail-review' || path.startsWith('/mail-review/')
}

function changedSessionError() {
  return new BrowserAuthError('Authentication session changed.', {
    code: 'session_changed',
    status: 401,
  })
}

export const setAccessToken = () => {}
export const getAccessToken = () => null

api.interceptors.request.use(
  (config) => {
    const method = (config.method || 'GET').toUpperCase()

    if (usesCookieAuthentication(config)) {
      config._authGeneration = getAuthGeneration()
    }

    if (SAFE_METHODS.has(method) || !usesCookieAuthentication(config)) {
      deleteHeader(config.headers, CSRF_HEADER)
      return config
    }

    const token = getCsrfToken()
    if (!token) {
      requireReauthentication('csrf_token_missing')
      throw new MissingCsrfTokenError()
    }

    setHeader(config.headers, CSRF_HEADER, token)
    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

api.interceptors.response.use(
  (response) => {
    if (
      usesCookieAuthentication(response.config) &&
      response.config?._authGeneration !== getAuthGeneration()
    ) {
      return Promise.reject(
        new BrowserAuthError('Stale authenticated response was discarded.', {
          code: 'stale_response',
        }),
      )
    }

    return response
  },
  async (error) => {
    const originalRequest = error.config
    const status = error.response?.status
    const errorCode = error.response?.data?.error

    if (!originalRequest || !usesCookieAuthentication(originalRequest)) {
      return Promise.reject(error)
    }

    if (status === 403 && errorCode === 'account_inactive') {
      purgeProtectedQueryCache()
      requireReauthentication('account_inactive')
      return Promise.reject(error)
    }

    if (
      status === 403 &&
      (errorCode === 'authorization_denied' || hasPrivilegedPath(originalRequest))
    ) {
      purgeProtectedQueryCache()
      notifyAuthorizationChange('authorization_denied')
      return Promise.reject(error)
    }

    if (status === 404 && isMailReviewRequest(originalRequest)) {
      purgeProtectedQueryCache()
      notifyAuthorizationChange('mail_review_disabled')
      return Promise.reject(error)
    }

    if (status === 419 && errorCode === 'csrf_failed') {
      purgeProtectedQueryCache()
      requireReauthentication('csrf_failed')
      return Promise.reject(error)
    }

    if (status !== 401) return Promise.reject(error)

    purgeProtectedQueryCache()
    const currentGeneration = getAuthGeneration()
    const requestGeneration = originalRequest._authGeneration

    if (requestGeneration !== currentGeneration) {
      if (
        originalRequest._authGenerationRetry ||
        !hasActiveBrowserSession()
      ) {
        return Promise.reject(changedSessionError())
      }

      originalRequest._authGenerationRetry = true
      originalRequest._authGeneration = currentGeneration
      return api.request(originalRequest)
    }

    if (
      originalRequest._authGenerationRetry ||
      originalRequest._authRefreshAttempted
    ) {
      requireReauthentication(errorCode || 'session_changed')
      return Promise.reject(changedSessionError())
    }

    originalRequest._authRefreshAttempted = true

    try {
      await refreshBrowserSession()

      if (originalRequest._authGeneration !== getAuthGeneration()) {
        if (
          originalRequest._authGenerationRetry ||
          !hasActiveBrowserSession()
        ) {
          return Promise.reject(changedSessionError())
        }

        originalRequest._authGenerationRetry = true
      }

      originalRequest._authGeneration = getAuthGeneration()
      notifyAuthorizationChange('session_refreshed')
      return api.request(originalRequest)
    } catch (refreshError) {
      requireReauthentication(
        refreshError?.code === 'csrf_token_missing' ||
          refreshError?.code === 'refresh_blocked'
          ? refreshError.code
          : 'session_changed',
      )
      return Promise.reject(refreshError)
    }
  },
)

export default api

// API methods
export const apiMethods = {
  // Auth methods
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  logout: () => logoutBrowserSession(),
  refresh: () => refreshBrowserSession(),
  me: () => api.get('/auth/me'),

  // Page methods
  getPages: () => api.get('/pages'),
  getPage: (id) => api.get(`/pages/${id}`),
  createPage: (data) => api.post('/pages', data),
  updatePage: (id, data) => api.put(`/pages/${id}`, data),
  deletePage: (id) => api.delete(`/pages/${id}`),
  publishPage: (id) => api.post(`/pages/${id}/publish`),
  unpublishPage: (id) => api.post(`/pages/${id}/unpublish`),

  // Block methods
  getBlocks: (pageId, viewMode = 'live', locale = 'en') =>
    api.get('/blocks', {
      params: { page_id: pageId, view_mode: viewMode, locale },
    }),

  getBlock: (id) => api.get(`/blocks/${id}`),
  createBlock: (data) => api.post('/blocks', data),
  updateBlock: (id, data) => api.put(`/blocks/${id}`, data),
  deleteBlock: (id, locale = 'en') =>
    api.delete(`/blocks/${id}`, { params: { locale } }),
  publishBlock: (id) => api.post(`/blocks/${id}/publish`),
  unpublishBlock: (id) => api.post(`/blocks/${id}/unpublish`),
  publishAllRevisions: () => api.post('/blocks/publish-all-revisions'),

  // Block revision methods
  getBlockRevisions: (blockId, locale = 'en', limit = 10) =>
    api.get(`/blocks/${blockId}/revisions`, {
      params: { locale, limit },
    }),

  rollbackToRevision: (blockId, revisionId, locale) =>
    api.post(`/blocks/${blockId}/revisions/${revisionId}/rollback`, {
      locale,
    }),

  deleteUnpublishedRevisions: (blockId, locale) =>
    api.delete(`/blocks/${blockId}/unpublished-revisions`, {
      params: { locale },
    }),

  // Block type methods
  getBlockTypes: () => api.get('/block-types'),
  getBlockType: (id) => api.get(`/block-types/${id}`),
  createBlockType: (data) => api.post('/block-types', data),
  updateBlockType: (id, data) => api.put(`/block-types/${id}`, data),
  deleteBlockType: (id) => api.delete(`/block-types/${id}`),

  // Language methods
  getLanguages: () => api.get('/languages'),
  getLanguage: (id) => api.get(`/languages/${id}`),
  createLanguage: (data) => api.post('/languages', data),
  updateLanguage: (id, data) => api.put(`/languages/${id}`, data),
  deleteLanguage: (id) => api.delete(`/languages/${id}`),

  // Field type methods
  getFieldTypes: () => api.get('/field-types'),
  getFieldType: (id) => api.get(`/field-types/${id}`),
  createFieldType: (data) => api.post('/field-types', data),
  updateFieldType: (id, data) => api.put(`/field-types/${id}`, data),
  deleteFieldType: (id) => api.delete(`/field-types/${id}`),

  // User management methods
  getUsers: (params) => api.get('/users', { params }).then((res) => res.data),
  getUser: (id) => api.get(`/users/${id}`).then((res) => res.data),
  createUser: (data) => api.post('/users', data).then((res) => res.data),
  updateUser: (id, data) =>
    api.put(`/users/${id}`, data).then((res) => res.data),
  deleteUser: (id) => api.delete(`/users/${id}`).then((res) => res.data),
  updateUserRole: (id, role) =>
    api.patch(`/users/${id}/role`, { role }).then((res) => res.data),
  toggleUserStatus: (id) =>
    api.patch(`/users/${id}/status`).then((res) => res.data),

  // Role methods
  getRoles: () => api.get('/roles').then((res) => res.data),
  getRole: (id) => api.get(`/roles/${id}`).then((res) => res.data),

  // Menu permission methods
  getMenuPermissions: () =>
    api.get('/menu-permissions').then((res) => res.data),
  getAvailableMenus: () =>
    api.get('/menu-permissions/available-menus').then((res) => res.data),
  updateMenuPermissions: (updates) =>
    api
      .post('/menu-permissions/bulk-update', { updates })
      .then((res) => res.data),

  // Settings methods
  getSettings: () => api.get('/settings').then((res) => res.data),
  getUrlSlugs: () => api.get('/settings/url-slugs').then((res) => res.data),
  updateUrlSlugs: (data) =>
    api.put('/settings/url-slugs', data).then((res) => res.data),
  updateSettings: (settings) =>
    api.put('/settings', { settings }).then((res) => res.data),

  // Ecommerce - Products
  getProducts: (params) => api.get('/ecommerce/products', { params }),
  getProduct: (id) => api.get(`/ecommerce/products/${id}`),
  createProduct: (data) => api.post('/ecommerce/products', data),
  updateProduct: (id, data) => api.put(`/ecommerce/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/ecommerce/products/${id}`),
  publishProduct: (id) => api.post(`/ecommerce/products/${id}/publish`),
  archiveProduct: (id) => api.post(`/ecommerce/products/${id}/archive`),

  // Ecommerce - Discounts
  getDiscounts: (params) => api.get('/ecommerce/discounts', { params }),
  getDiscount: (id) => api.get(`/ecommerce/discounts/${id}`),
  createDiscount: (data) => api.post('/ecommerce/discounts', data),
  updateDiscount: (id, data) => api.put(`/ecommerce/discounts/${id}`, data),
  deleteDiscount: (id) => api.delete(`/ecommerce/discounts/${id}`),

  // Ecommerce - Coupons
  getCoupons: (params) => api.get('/ecommerce/coupons', { params }),
  getCoupon: (id) => api.get(`/ecommerce/coupons/${id}`),
  createCoupon: (data) => api.post('/ecommerce/coupons', data),
  updateCoupon: (id, data) => api.put(`/ecommerce/coupons/${id}`, data),
  deleteCoupon: (id) => api.delete(`/ecommerce/coupons/${id}`),

  // Content Templates
  getContentTemplates: (params) => api.get('/content-templates', { params }),
  getContentTemplate: (id) => api.get(`/content-templates/${id}`),
  createContentTemplate: (data) => api.post('/content-templates', data),
  updateContentTemplate: (id, data) =>
    api.put(`/content-templates/${id}`, data),
  deleteContentTemplate: (id) => api.delete(`/content-templates/${id}`),

  // Block Presets
  getBlockPresets: (params) => api.get('/block-presets', { params }),
  getBlockPreset: (id, params) => api.get(`/block-presets/${id}`, { params }),
  createBlockPreset: (data) => api.post('/block-presets', data),
  updateBlockPreset: (id, data) => api.put(`/block-presets/${id}`, data),
  deleteBlockPreset: (id) => api.delete(`/block-presets/${id}`),
  createBlockFromPreset: (data) => api.post('/blocks/create-from-preset', data),
  detachBlockPreset: (blockId) => api.post(`/blocks/${blockId}/detach-preset`),

  // Ecommerce - Orders
  getOrders: (params) => api.get('/ecommerce/orders', { params }),
  getOrder: (id) => api.get(`/ecommerce/orders/${id}`),
  updateOrderStatus: (id, data) =>
    api.put(`/ecommerce/orders/${id}/status`, data),
  refundOrder: (id, data) => api.post(`/ecommerce/orders/${id}/refund`, data),

  // Leads
  getLeads: (params) => api.get('/leads', { params }).then((res) => res.data),
  getLeadEvents: (params) =>
    api.get('/leads/events', { params }).then((res) => res.data),
  getLead: (id) => api.get(`/leads/${id}`).then((res) => res.data),
  deleteLead: (id) => api.delete(`/leads/${id}`).then((res) => res.data),
  updateLeadStage: (id, payload) =>
    api.patch(`/leads/${id}/stage`, payload).then((res) => res.data),
  dispatchWpLeadSync: (payload) =>
    api.post('/leads/wp-sync/dispatch', payload).then((res) => res.data),

  // Mail Review
  getMailReviewItems: (
    { page, per_page, decision_status, advisory_label },
    signal,
  ) => {
    const params = { page, per_page, decision_status }
    if (advisory_label !== undefined) {
      params.advisory_label = advisory_label
    }

    return api
      .get('/mail-review/items', { params, signal })
      .then((res) => res.data)
  },
  getMailReviewItem: (reviewId, signal) =>
    api.get(mailReviewItemPath(reviewId), { signal }).then((res) => res.data),
  decideMailReviewItem: (reviewId, payload) =>
    api
      .post(
        `${mailReviewItemPath(reviewId)}/decision`,
        {
          decision: payload?.decision,
          expected_version: payload?.expected_version,
          idempotency_key: payload?.idempotency_key,
        },
        { headers: { 'X-Mail-Review-Intent': 'decision' } },
      )
      .then((res) => res.data),

  // Maintenance
  getMaintenanceSites: (params) =>
    api.get('/maintenance/sites', { params }).then((res) => res.data),
  getMaintenanceSite: (id) =>
    api.get(`/maintenance/sites/${id}`).then((res) => res.data),
  getMaintenanceSitePlugins: (id, params) =>
    api.get(`/maintenance/sites/${id}/plugins`, { params }).then((res) => res.data),
  updateMaintenanceSiteMigrationSettings: (id, payload) =>
    api.patch(`/maintenance/sites/${id}/migration-settings`, payload).then((res) => res.data),
  requestMaintenanceReport: (id) =>
    api.post(`/maintenance/sites/${id}/request-report`).then((res) => res.data),
  updateMaintenancePlugin: (id, pluginId) =>
    api.post(`/maintenance/sites/${id}/plugins/${pluginId}/update`).then((res) => res.data),
  deleteMaintenanceSite: (id) =>
    api.delete(`/maintenance/sites/${id}`).then((res) => res.data),
  getMaintenanceActivityReport: (id, params) =>
    api.get(`/maintenance/sites/${id}/activity-report`, { params }).then((res) => res.data),
  downloadMaintenanceVersionChanges: (id, params) =>
    api.get(`/maintenance/sites/${id}/version-changes`, {
      params,
      responseType: 'blob',
    }),
  getMaintenanceRegistrationTokens: () =>
    api.get('/maintenance/registration-tokens').then((res) => res.data),
  createMaintenanceRegistrationToken: (payload) =>
    api.post('/maintenance/registration-tokens', payload).then((res) => res.data),
  deleteMaintenanceRegistrationToken: (id) =>
    api.delete(`/maintenance/registration-tokens/${id}`).then((res) => res.data),
  getMaintenanceProjects: () =>
    api.get('/maintenance/projects').then((res) => res.data),
  createMaintenanceProject: (payload) =>
    api.post('/maintenance/projects', payload).then((res) => res.data),
  getMaintenanceMigrationGrants: (params) =>
    api.get('/maintenance/migration-grants', { params }).then((res) => res.data),
  createMaintenanceMigrationGrant: (payload) =>
    api.post('/maintenance/migration-grants', payload).then((res) => res.data),
  deleteMaintenanceMigrationGrant: (id) =>
    api.delete(`/maintenance/migration-grants/${id}`).then((res) => res.data),
  getMaintenanceMigrations: (params) =>
    api.get('/maintenance/migrations', { params }).then((res) => res.data),
  cancelMaintenanceMigration: (id) =>
    api.post(`/maintenance/migrations/${id}/cancel`).then((res) => res.data),
}
